import { useState } from 'react'
import { Card, Row, Col, Badge, Button, Table, Spinner, Alert, Modal, Form } from 'react-bootstrap'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { toast } from 'react-toastify'
import api from '../../services/api'
import { formatDateOnlyEsMX } from '../../utils/apiDateFormat'
import MatchingInterface from './MatchingInterface'
import { formatCurrency } from '../../utils/formatters'

const ReconciliationDetail = ({ reconciliation, onClose, onRefresh }) => {
  const queryClient = useQueryClient()
  const [showImportModal, setShowImportModal] = useState(false)
  const [selectedFile, setSelectedFile] = useState(null)
  const [selectedFormat, setSelectedFormat] = useState(null)
  const [showMatching, setShowMatching] = useState(false)

  // Obtener movimientos de la conciliación
  const { data: movimientos, isLoading: loadingMovimientos } = useQuery(
    ['reconciliation-movements', reconciliation?.id],
    async () => {
      if (!reconciliation?.id) return []
      const response = await api.get(`/financial/movimientos-bancarios/?conciliacion_id=${reconciliation.id}`)
      return response.data.results || response.data || []
    },
    { enabled: !!reconciliation?.id }
  )

  // Obtener formatos bancarios
  const { data: formatos } = useQuery(
    'bank-formats',
    async () => {
      const response = await api.get('/financial/formatos-bancarios/')
      return response.data.results || response.data || []
    }
  )

  // Mutación para importar archivo
  const importMutation = useMutation(
    async (formData) => {
      const response = await api.post(
        `/financial/bank-reconciliations/${reconciliation.id}/importar/`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      )
      return response.data
    },
    {
      onSuccess: (data) => {
        toast.success(`${data.total || 0} movimientos importados exitosamente`)
        queryClient.invalidateQueries(['reconciliation-movements', reconciliation.id])
        queryClient.invalidateQueries('bank-reconciliations')
        setShowImportModal(false)
        setSelectedFile(null)
        setSelectedFormat(null)
        if (onRefresh) onRefresh()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al importar archivo'
        toast.error(errorMsg)
      }
    }
  )

  // Mutación para ejecutar matching automático
  const matchingMutation = useMutation(
    async (rangoDias = 3) => {
      const response = await api.get(
        `/financial/bank-reconciliations/${reconciliation.id}/matches/`,
        { params: { rango_dias: rangoDias } }
      )
      return response.data
    },
    {
      onSuccess: (data) => {
        toast.success(`Matching completado: ${data.total_matches || 0} matches encontrados`)
        queryClient.invalidateQueries(['reconciliation-movements', reconciliation.id])
        setShowMatching(true)
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al ejecutar matching'
        toast.error(errorMsg)
      }
    }
  )

  // Mutación para cerrar conciliación
  const cerrarMutation = useMutation(
    async () => {
      const response = await api.post(`/financial/bank-reconciliations/${reconciliation.id}/cerrar/`)
      return response.data
    },
    {
      onSuccess: () => {
        toast.success('Conciliación cerrada exitosamente')
        queryClient.invalidateQueries('bank-reconciliations')
        if (onRefresh) onRefresh()
        if (onClose) onClose()
      },
      onError: (error) => {
        const errorMsg = error.response?.data?.error || 'Error al cerrar conciliación'
        toast.error(errorMsg)
      }
    }
  )

  const handleImport = () => {
    if (!selectedFile) {
      toast.error('Debes seleccionar un archivo')
      return
    }

    const formData = new FormData()
    formData.append('archivo', selectedFile)
    if (selectedFormat) {
      formData.append('formato_bancario_id', selectedFormat)
    }

    importMutation.mutate(formData)
  }

  const handleCerrar = () => {
    if (window.confirm('¿Está seguro de que desea cerrar esta conciliación? Esta acción no se puede deshacer.')) {
      cerrarMutation.mutate()
    }
  }

  const handleExportar = () => {
    const formato = window.prompt('Seleccione formato:\n1. Excel (xlsx)\n2. CSV\n\nIngrese 1 o 2:', '1')
    if (!formato) return
    
    const formatoParam = formato === '1' ? 'excel' : 'csv'
    const url = `/api/financial/bank-reconciliations/${reconciliation.id}/exportar/?formato=${formatoParam}`
    
    // Crear enlace temporal para descarga
    const link = document.createElement('a')
    link.href = url
    link.download = `movimientos_conciliacion_${reconciliation.id}.${formatoParam === 'excel' ? 'xlsx' : 'csv'}`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    toast.success('Exportación iniciada')
  }

  const handleReporte = () => {
    const url = `/api/financial/bank-reconciliations/${reconciliation.id}/reporte/`
    
    // Crear enlace temporal para descarga
    const link = document.createElement('a')
    link.href = url
    link.download = `reporte_conciliacion_${reconciliation.id}.pdf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    
    toast.success('Generando reporte PDF...')
  }

  const getEstadoBadge = (estado) => {
    const badges = {
      'abierta': { bg: 'info', text: 'Abierta' },
      'en_proceso': { bg: 'warning', text: 'En Proceso' },
      'cerrada': { bg: 'success', text: 'Cerrada' }
    }
    const badge = badges[estado] || { bg: 'secondary', text: estado }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  const getMovimientoEstadoBadge = (estado) => {
    const badges = {
      'pendiente': { bg: 'secondary', text: 'Pendiente' },
      'propuesto': { bg: 'info', text: 'Propuesto' },
      'conciliado': { bg: 'success', text: 'Conciliado' },
      'descartado': { bg: 'danger', text: 'Descartado' }
    }
    const badge = badges[estado] || { bg: 'secondary', text: estado }
    return <Badge bg={badge.bg}>{badge.text}</Badge>
  }

  if (!reconciliation) return null

  const movimientosList = movimientos || []
  const movimientosPendientes = movimientosList.filter(m => m.estado === 'pendiente').length
  const movimientosConciliados = movimientosList.filter(m => m.estado === 'conciliado').length
  const movimientosDescartados = movimientosList.filter(m => m.estado === 'descartado').length

  return (
    <>
      <Card>
        <Card.Header className="d-flex justify-content-between align-items-center">
          <div>
            <h5 className="mb-0">Detalle de Conciliación</h5>
            <small className="text-muted">
              {reconciliation.bank_account_name} - {formatDateOnlyEsMX(reconciliation.reconciliation_date, {}, '—')}
            </small>
          </div>
          <Button variant="outline-secondary" size="sm" onClick={onClose}>
            <i className="bi bi-x-lg"></i> Cerrar
          </Button>
        </Card.Header>
        <Card.Body>
          {/* Información de la conciliación */}
          <Row className="mb-4">
            <Col md={3}>
              <Card className="bg-light">
                <Card.Body>
                  <Card.Title className="text-muted small mb-1">Estado</Card.Title>
                  <div>{getEstadoBadge(reconciliation.estado)}</div>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="bg-light">
                <Card.Body>
                  <Card.Title className="text-muted small mb-1">Saldo Estado de Cuenta</Card.Title>
                  <h5 className="mb-0">
                    {formatCurrency(reconciliation.statement_balance || 0)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="bg-light">
                <Card.Body>
                  <Card.Title className="text-muted small mb-1">Saldo en Libros</Card.Title>
                  <h5 className="mb-0">
                    {formatCurrency(reconciliation.book_balance || 0)}
                  </h5>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className={`bg-light ${Math.abs(reconciliation.difference || 0) < 0.01 ? 'border-success' : 'border-warning'}`}>
                <Card.Body>
                  <Card.Title className="text-muted small mb-1">Diferencia</Card.Title>
                  <h5 className={`mb-0 ${Math.abs(reconciliation.difference || 0) < 0.01 ? 'text-success' : 'text-warning'}`}>
                    {formatCurrency(Math.abs(reconciliation.difference || 0))}
                  </h5>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Estadísticas de movimientos */}
          <Row className="mb-4">
            <Col md={3}>
              <Card>
                <Card.Body>
                  <Card.Title className="text-muted small">Total Movimientos</Card.Title>
                  <h3>{movimientosList.length}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card>
                <Card.Body>
                  <Card.Title className="text-muted small">Pendientes</Card.Title>
                  <h3 className="text-warning">{movimientosPendientes}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card>
                <Card.Body>
                  <Card.Title className="text-muted small">Conciliados</Card.Title>
                  <h3 className="text-success">{movimientosConciliados}</h3>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card>
                <Card.Body>
                  <Card.Title className="text-muted small">Descartados</Card.Title>
                  <h3 className="text-danger">{movimientosDescartados}</h3>
                </Card.Body>
              </Card>
            </Col>
          </Row>

          {/* Acciones */}
          <div className="d-flex gap-2 mb-4">
            <Button
              variant="primary"
              onClick={() => setShowImportModal(true)}
              disabled={reconciliation.estado === 'cerrada'}
            >
              <i className="bi bi-upload me-1"></i>
              Importar Archivo
            </Button>
            <Button
              variant="info"
              onClick={() => matchingMutation.mutate(3)}
              disabled={matchingMutation.isLoading || reconciliation.estado === 'cerrada' || movimientosPendientes === 0}
            >
              {matchingMutation.isLoading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Ejecutando...
                </>
              ) : (
                <>
                  <i className="bi bi-search me-1"></i>
                  Ejecutar Matching Automático
                </>
              )}
            </Button>
            <Button
              variant="success"
              onClick={() => setShowMatching(true)}
              disabled={reconciliation.estado === 'cerrada'}
            >
              <i className="bi bi-list-check me-1"></i>
              Ver/Editar Matches
            </Button>
            <Button
              variant="info"
              onClick={handleExportar}
              disabled={reconciliation.estado === 'cerrada' || movimientosList.length === 0}
            >
              <i className="bi bi-download me-1"></i>
              Exportar Movimientos
            </Button>
            <Button
              variant="secondary"
              onClick={handleReporte}
              disabled={reconciliation.estado === 'cerrada'}
            >
              <i className="bi bi-file-pdf me-1"></i>
              Generar Reporte PDF
            </Button>
            {reconciliation.estado !== 'cerrada' && (
              <Button
                variant="warning"
                onClick={handleCerrar}
                disabled={cerrarMutation.isLoading}
              >
                {cerrarMutation.isLoading ? (
                  <>
                    <Spinner size="sm" className="me-1" />
                    Cerrando...
                  </>
                ) : (
                  <>
                    <i className="bi bi-lock me-1"></i>
                    Cerrar Conciliación
                  </>
                )}
              </Button>
            )}
          </div>

          {/* Lista de movimientos */}
          <Card>
            <Card.Header>
              <h6 className="mb-0">Movimientos Bancarios</h6>
            </Card.Header>
            <Card.Body>
              {loadingMovimientos ? (
                <div className="text-center py-4">
                  <Spinner animation="border" />
                </div>
              ) : movimientosList.length > 0 ? (
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Fecha</th>
                      <th>Descripción</th>
                      <th>Monto</th>
                      <th>Referencia</th>
                      <th>Estado</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimientosList.map((movimiento) => (
                      <tr key={movimiento.id}>
                        <td>{formatDateOnlyEsMX(movimiento.fecha, {}, '—')}</td>
                        <td>{movimiento.descripcion || 'N/A'}</td>
                        <td className={movimiento.monto >= 0 ? 'text-success' : 'text-danger'}>
                          {formatCurrency(Math.abs(parseFloat(movimiento.monto || 0)))}
                        </td>
                        <td>{movimiento.referencia || 'N/A'}</td>
                        <td>{getMovimientoEstadoBadge(movimiento.estado)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <Alert variant="info">
                  No hay movimientos importados. Importa un archivo para comenzar.
                </Alert>
              )}
            </Card.Body>
          </Card>
        </Card.Body>
      </Card>

      {/* Modal de importación */}
      <Modal show={showImportModal} onHide={() => { setShowImportModal(false); setSelectedFile(null); setSelectedFormat(null) }}>
        <Modal.Header closeButton>
          <Modal.Title>Importar Movimientos Bancarios</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Formato Bancario (Opcional)</Form.Label>
              <Form.Select
                value={selectedFormat || ''}
                onChange={(e) => setSelectedFormat(e.target.value || null)}
              >
                <option value="">Seleccionar formato...</option>
                {formatos?.filter(f => f.activo).map(formato => (
                  <option key={formato.id} value={formato.id}>
                    {formato.nombre} ({formato.formato})
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                Si no selecciona un formato, el sistema intentará detectarlo automáticamente
              </Form.Text>
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Archivo *</Form.Label>
              <Form.Control
                type="file"
                accept=".csv,.ofx,.xml"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                required
              />
              <Form.Text className="text-muted">
                Formatos permitidos: CSV, OFX, XML
              </Form.Text>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => { setShowImportModal(false); setSelectedFile(null); setSelectedFormat(null) }}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={handleImport} disabled={!selectedFile || importMutation.isLoading}>
            {importMutation.isLoading ? (
              <>
                <Spinner size="sm" className="me-2" />
                Importando...
              </>
            ) : (
              'Importar'
            )}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Interfaz de matching */}
      {showMatching && (
        <MatchingInterface
          reconciliation={reconciliation}
          movimientos={movimientosList}
          onClose={() => setShowMatching(false)}
          onRefresh={() => {
            queryClient.invalidateQueries(['reconciliation-movements', reconciliation.id])
            if (onRefresh) onRefresh()
          }}
        />
      )}
    </>
  )
}

export default ReconciliationDetail
